import { Box, Typography } from "@mui/material";
import strings from "global/constants/StringConstants";
import { maskedContent } from "helpers/maskedContent";
import { safeFileName } from "screens/ContractCompare/ContractCompareTypesAndValidation";
import styles from "../ContractSearch.styles";
import {
  fill,
  matchingPassagesHeading,
  passageTag,
} from "../ContractSearchHelper";
import {
  CompareLock,
  MatchedContract,
  MatchedPassage,
} from "../ContractSearchTypes";
import { CompareSourceEntry } from "./CompareEntry";
import { ChevronDownGlyph } from "./ContractSearchIcons";

interface MatchedContractsListProps {
  /** Prefix for element ids, unique per AI turn. */
  idPrefix: string;
  contracts: MatchedContract[];
  /** Indexed documents in the repository — the "of N contracts" denominator. */
  totalIndexed: number;
  expanded: Record<string, boolean>;
  onToggle: (documentId: string) => void;
  /** The `passageKey` of the passage the viewer shows, as `onOpenPassage` received it. */
  activePassageKey: string | null;
  /**
   * `passageKey` names the passage button in the page (`data-passage-key`), so focus can return to it when the
   * viewer closes. It is built from the turn and the place of the contract and the passage, never from the document
   * id or the quote: analytics tools record the attributes of a clicked element.
   */
  onOpenPassage: (passage: MatchedPassage, contract: MatchedContract, passageKey: string) => void;
  /**
   * Opens Compare contract versions for this contract. When given, each row gets the compare control (PDF /
   * Word) or the "can't be compared" hint beside it; without it the list is unchanged.
   */
  onCompare?: (contract: MatchedContract) => void;
  /** Set when the plan lacks AI Assistant: the control looks locked, and `onCompare` still receives the click. */
  compareLock?: CompareLock | null;
  /**
   * Contracts whose compare control is already beside their name in the answer text (where the frames draw it):
   * their row does not repeat it. Every other row keeps it, so no matched contract is left without a way in.
   */
  compareSkipIds?: ReadonlySet<string>;
}

/**
 * "Matched contracts": one expandable row per document the search touched, with
 * its quoted passages underneath. Rows and passages are real buttons with
 * `aria-expanded` / `aria-controls`, so the whole block works from the keyboard.
 * The compare control sits beside a row, never inside it (a button in a button).
 */
const MatchedContractsList = ({
  idPrefix,
  contracts,
  totalIndexed,
  expanded,
  onToggle,
  activePassageKey,
  onOpenPassage,
  onCompare,
  compareLock,
  compareSkipIds,
}: MatchedContractsListProps) => {
  const copy = strings.contractSearch;
  if (!contracts.length) return null;

  return (
    <Box component="section" sx={styles.matchedCard} aria-label="Matched contracts">
      <Box sx={styles.matchedHeading}>
        <Typography component="h3" sx={styles.matchedHeadingLabel}>
          {copy.matchedHeading}
        </Typography>
        <Typography component="span" sx={styles.matchedHeadingCount}>
          {fill(copy.matchedCount, {
            matched: contracts.length,
            total: Math.max(totalIndexed, contracts.length),
          })}
        </Typography>
      </Box>

      {contracts.map((contract, index) => {
        const isOpen = !!expanded[contract.documentId];
        // From the row's place, never the document id: analytics tools record the `id` of a clicked element (D22, D59).
        const panelId = `${idPrefix}-passages-${index}`;
        const fileNameId = `${idPrefix}-file-${index}`;
        const hasPassages = contract.passages.length > 0;
        // A name can carry characters that reorder what is drawn (a right-to-left override could turn `.pdf` into
        // `.docx`), right beside the compare control: shown as the Compare page shows it. The extension checks
        // and the compare control keep reading the raw name.
        const shownName = safeFileName(contract.fileName);
        return (
          <Box key={contract.documentId}>
            <Box sx={styles.contractRowWrap}>
              <Box
                component="button"
                type="button"
                sx={styles.contractRow}
                // The row shows a file name and carries it as a `title`: masked from recording and autocapture.
                {...maskedContent}
                onClick={() => onToggle(contract.documentId)}
                aria-expanded={hasPassages ? isOpen : undefined}
                aria-controls={hasPassages ? panelId : undefined}
                disabled={!hasPassages}
              >
                <Box sx={styles.pdfTile} aria-hidden="true">
                  PDF
                </Box>
                <Box sx={styles.contractInfo}>
                  <Typography
                    component="span"
                    id={fileNameId}
                    sx={styles.contractFile}
                    title={shownName}
                  >
                    {shownName}
                  </Typography>
                  {contract.meta && (
                    <Typography component="span" sx={styles.contractMeta} display="block">
                      {contract.meta}
                    </Typography>
                  )}
                </Box>
                <Box component="span" sx={styles.contractTag(contract.used)}>
                  {passageTag(contract)}
                </Box>
                {hasPassages && <ChevronDownGlyph sx={styles.chevron(isOpen)} />}
              </Box>
              {onCompare && !compareSkipIds?.has(contract.documentId) && (
                <Box sx={styles.compareSlot}>
                  <CompareSourceEntry
                    contract={contract}
                    fileNameId={fileNameId}
                    lock={compareLock}
                    onCompare={onCompare}
                  />
                </Box>
              )}
            </Box>

            {hasPassages && isOpen && (
              <Box id={panelId} sx={styles.passagesWrap}>
                <Typography component="h4" sx={styles.passagesHeading}>
                  {matchingPassagesHeading(contract.passages.length)}
                </Typography>
                <Box component="ul" sx={styles.passagesList}>
                  {contract.passages.map((passage, passageIndex) => {
                    const passageKey = `${idPrefix}-passage-${index}-${passageIndex}`;
                    const active = activePassageKey === passageKey;
                    return (
                      <Box component="li" key={passageKey}>
                        <Box
                          component="button"
                          type="button"
                          data-passage-key={passageKey}
                          sx={styles.passage(active)}
                          // The button shows a clause of the contract: masked from recording and autocapture. It has
                          // no `aria-label`: its name is what it shows (page, lines, "Open in viewer", the clause),
                          // so it holds its visible text (WCAG 2.5.3).
                          {...maskedContent}
                          onClick={() => onOpenPassage(passage, contract, passageKey)}
                          aria-pressed={active}
                        >
                          <Box sx={styles.passageHead}>
                            {passage.page != null && (
                              <Typography component="span" sx={styles.passagePage}>
                                p. {passage.page}
                              </Typography>
                            )}
                            {passage.page != null && passage.linesLabel && (
                              <Box sx={styles.passageDot} aria-hidden="true" />
                            )}
                            {passage.linesLabel && (
                              <Typography component="span" sx={styles.passageLines}>
                                {passage.linesLabel}
                              </Typography>
                            )}
                            <Typography component="span" sx={styles.passageOpen}>
                              {copy.openInViewer}
                            </Typography>
                          </Box>
                          <Typography component="span" sx={styles.passageQuote} display="block">
                            {passage.quote}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default MatchedContractsList;
